<template>
  <BubbleMenu
    :editor="editor"
    :shouldShow="shouldShowLinkPopover"
    :options="{
      strategy: 'absolute',
      placement: 'top-start',
      inline: 'true',
    }"
  >
    <UseFocusTrap
      v-if="isOpen"
      :options="{
        // never search for first tabbable; we’ll steer focus ourselves
        initialFocus: false,
        // when nothing is tabbable, focus this element instead (prevents the error)
        fallbackFocus: () => trapRoot || undefined,
        clickOutsideDeactivates: true,
        escapeDeactivates: true,
        setReturnFocus: () => editor?.view?.dom
      }"
    >
      <!-- make the trap container itself focusable -->
      <div
        ref="trapRoot"
        class="bubble-menu"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        @keydown.esc.prevent="() => { editor?.commands.focus(); }"
      >
        <input
          ref="firstInput"
          class="bubble-link"
          v-model="link.href"
          @keydown.enter="applyLink"
        />

        <div class="actions">
          <i-button :disabled="false" :title="__('Apply Link')" class="i-btn btn" @click.prevent="applyLink">
            <i-icon :icon="'turn-down-left'" type="far" size="medium" :name="__('Apply Link')" />
          </i-button>

          <i-button :disabled="false" :title="__('Advanced Edit')" class="i-btn btn"
            @click.prevent="$emit('toggleLinkModal', true)">
            <i-icon :icon="'pen-to-square'" type="far" size="medium" :name="__('Advanced Edit')" />
          </i-button>

          <i-button :disabled="false" :title="__('Delete Link')" class="i-btn btn" @click.prevent="removeLink">
            <i-icon :icon="'trash'" type="far" size="medium" :name="__('Delete Link')" />
          </i-button>
        </div>
      </div>
    </UseFocusTrap>
  </BubbleMenu>
</template>

<script setup>
import { ref, shallowRef, computed, watch, nextTick } from 'vue'
import IButton from './lib/button/Button.vue'
import { UseFocusTrap } from '@vueuse/integrations/useFocusTrap/component';
import { BubbleMenu } from '@tiptap/vue-3/menus'
import globals from '@/globals.js'

function __(str) { return globals.I.gt.__(str) }

const emit = defineEmits(['update:show', 'toggleLinkModal'])
const props = defineProps({ editor: { type: Object, required: true } })
const editorRef = shallowRef(props.editor)

const link = computed(() => {
  if (!editorRef.value) return { href: '' }
  // ensure we always have an object, so v-model never hits undefined
  const attrs = editorRef.value.getAttributes('link') || {}
  return { href: attrs.href ?? '' }
})

function applyLink() {
  if (!editorRef.value) return
  editorRef.value.chain().focus().extendMarkRange('link').setLink({ href: link.value.href }).run()
  const { to } = editorRef.value.state.selection
  editorRef.value.chain().setTextSelection(to).unsetMark('link').run()
}

function removeLink() {
  if (!editorRef.value) return
  editorRef.value.chain().focus().extendMarkRange('link').unsetLink().run()
}

const isOpen = ref(false)
function shouldShowLinkPopover ({ editor, view }) {
  const focused = view?.hasFocus() || editor?.isFocused
  const active = !!focused && editor.isActive('link')
  if (isOpen.value !== active) isOpen.value = active
  return active
}

watch(isOpen, async v => {
  if (v) {
    await nextTick()
    editorRef.value?.commands.setMeta('bubbleMenu', 'updatePosition')
  }
})

const firstInput = ref(null)
const trapRoot = ref(null)

async function focus ({ activateTrap, select } = {}) {
  if (!isOpen.value) {
    editorRef.value?.commands.focus()
    editorRef.value?.commands.setMeta('bubbleMenu', 'updatePosition')
    await nextTick()
  }
  await nextTick()
  const target = firstInput.value
  if (target) {
    target.focus({ preventScroll: true })
    if (select) target.select?.()
  } else {
    // fallback focus if input isn’t there yet
    trapRoot.value?.focus?.({ preventScroll: true }) ||
      editorRef.value?.view?.dom?.focus({ preventScroll: true })
  }
}

defineExpose({ focus })
</script>

<style scoped>
.bubble-menu {
  background: rgba(255, 255, 255, 1);
  padding: 8px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}
.i-btn { margin: 0 0 0 2px; }
.bubble-link {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 13px;
  width: 200px;
  outline: none;
  transition: border-color 0.2s ease;
}
.bubble-link:focus { border-color: #1c385f; }
</style>
